// @flow
import React, { type Node } from 'react';
import { browserHistory } from 'react-router';
import { compose, pure } from 'recompose';
import { connect } from 'react-redux';
import { hot } from 'react-hot-loader';
import { get } from 'lodash';
import { analytics } from 'angieslist-app-platform';
import { Alert, Grid, Row, Col } from 'angieslist-react-components';
import Helmet from 'react-helmet-async';
import Menu from './components/Menu';
import Content from './components/Content';
import profileActions from '../common/profileActions';
import imageActions from '../../../app/common/images/imagesActions';
import type { ServiceProviderCacheState, UserState } from '../../../app/types';
import type { GetServiceProviderState } from '../../../app/common/serviceProvider/serviceProviderReducer';
import './EditProfile.scss';
import MiniProfile from './components/MiniProfile';
import type { ProfileReducerState } from '../common/profileReducer';
import menuAnalytics from './components/menuAnalytics';
import { FREE_UPGRADE_STATE, getServiceProviderUpgradeState } from '../../home/common/selectors/upgradeStateSelectors';
import type { UpgradeStateType } from '../../home/common/selectors/upgradeStateSelectors';

export type Props = {
  params: {
    serviceProviderId: string,
    uuid: string,
    page: string,
    section: string,
  },
  serviceProviderProfile: ProfileReducerState,
  serviceProviderCache: ServiceProviderCacheState,
  serviceProvider: GetServiceProviderState,
  getServiceProviderCurrentProfile: typeof profileActions.getServiceProviderCurrentProfile,
  getServiceProviderProfile: typeof profileActions.getServiceProviderProfile,
  getServiceProviderProfileCompletion: typeof profileActions.getServiceProviderProfileCompletion,
  getUUIDBySpId: typeof profileActions.getUUIDBySpId,
  user: UserState,
  experiment: any,
  config: any,
  serviceProviderId: string | number,
  serviceProviderUpgradeState: UpgradeStateType,
};

type State = {
  coverPhotoRenderError: boolean,
  sizeError: boolean
}

export class EditProfileViewComponent extends React.Component<Props, State> {
  state: State = { coverPhotoRenderError: false, sizeError: false }

  componentDidMount() {
    const { uuid } = get(this.props.serviceProviderCache, this.props.params.serviceProviderId, {});
    if (!uuid) {
      this.props.getUUIDBySpId(this.props.params.serviceProviderId);
    } else {
      this.props.getServiceProviderProfile({ uuid });
    }
    window.addEventListener('resize', this.resize);
  }

  componentDidUpdate() {
    const { isNorthStar, isAdvertiser } =
      get(this.props.serviceProviderCache, this.props.params.serviceProviderId, {});
    const { page } = this.props.params;
    const upsellPages = ['showcase', 'photos'];
    const editProfileContent = document.getElementById('Edit-Profile-Content');
    const { serviceProviderUpgradeState } = this.props;
    const freePros = serviceProviderUpgradeState === FREE_UPGRADE_STATE;

    if (editProfileContent) {
      if (upsellPages.includes(page) && !isAdvertiser && isNorthStar && freePros) {
        editProfileContent.classList.add('upsell');
      } else {
        editProfileContent.classList.remove('upsell');
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  setCoverPhotoRenderError: ((boolean) => void) = (sizeError) => {
    this.setState({ coverPhotoRenderError: true, sizeError });
  }
  resize: (() => void) = () => this.forceUpdate();
  resetCoverPhotoRenderError: (() => void) = () => {
    this.setState({ coverPhotoRenderError: false, sizeError: false });
  }

  props: Props;
  render(): Node {
    const {
      params: {
        page,
        serviceProviderId,
        section,
      },
      serviceProviderCache,
      serviceProvider,
      user,
      experiment,
      config,
    } = this.props;

    switch (page) {
      case 'business-details':
      case 'business-highlights':
        browserHistory.push(`/app/profile/${serviceProviderId}/about`);
        break;
      case 'services-offered':
      case 'health-and-safety':
      case 'general-information':
        browserHistory.push(`/app/profile/${serviceProviderId}/service-details`);
        break;
      default:
    }

    const { coverPhotoRenderError, sizeError } = this.state;

    const {
      isAdvertiser, profileUrl, isNorthStar, uuid,
    } = get(serviceProviderCache, serviceProviderId, {});
    const currentWindowWith = window.innerWidth;
    const screenXSEnds = 768;
    const mobileUser = currentWindowWith < screenXSEnds;

    const entitlements = get(user, 'userInfo.response.entitlements', []);
    const spEntitlements = entitlements.filter(x => x.scope === 'ServiceProvider' && x.scopeId === serviceProviderId);

    const userId = get(user, 'userInfo.response.userId', false);

    const { accountingSyncUrl } = config;

    return (
      <Grid id="Edit-Profile-View">
        <Helmet title="My Business" />
        <Choose>
          <When condition={serviceProvider.error}>
            <Alert bsStyle="danger">Unable to retrieve information for this business.  Please use your browser&apos;s Back button and try again.</Alert>
          </When>
          <Otherwise>
            <Row>
              <Col id="Edit-Profile-Menu" xs={12} sm={4}>
                <div className={mobileUser && page ? 'hidden-xs' : null}>
                  <MiniProfile
                    coverPhotoRenderError={coverPhotoRenderError}
                    resetCoverPhotoRenderError={this.resetCoverPhotoRenderError}
                    setCoverPhotoRenderError={this.setCoverPhotoRenderError}
                    sizeError={sizeError}
                    uuid={uuid}
                  />
                  <div className="Edit-Profile-Card" style={{ marginTop: '1em' }}>
                    <Menu
                      {...{
                        serviceProviderId,
                        isAdvertiser,
                        isNorthStar,
                        mobileUser,
                      }}
                      entitlements={spEntitlements}
                      experiment={experiment}
                      currentPage={page}
                      accountingSyncUrl={accountingSyncUrl}
                    />
                  </div>
                </div>
              </Col>
              <div className={mobileUser && !page ? 'hidden-xs' : null}>
                <Col id="Edit-Profile-Content" className="Edit-Profile-Card" xs={12} sm={8}>
                  <Content
                    contentToDisplay="contentToDisplay"
                    activeMenuItem={page || 'about'}
                    {...{
                      section,
                      serviceProviderId,
                      mobileUser,
                      userId,
                      isAdvertiser,
                      isNorthStar,
                      profileUrl,
                    }}
                  />
                </Col>
              </div>
            </Row>
          </Otherwise>
        </Choose>
      </Grid>
    );
  }
}

export default (hot(module)(compose(
  analytics({ ruleset: menuAnalytics }),
  connect(state => ({
    serviceProviderCache: state.serviceProviderCache,
    serviceProvider: state.serviceProviderProfile.currentProfileUAPI,
    serviceProviderProfile: state.serviceProviderProfile,
    user: state.user,
    experiment: state.experimentV2,
    config: state.config,
    serviceProviderUpgradeState: getServiceProviderUpgradeState(state),
  }), {
    ...profileActions,
    ...imageActions,
  }),
  pure,
)(EditProfileViewComponent)): any);



// WEBPACK FOOTER //
// ./src/modules/profile/browser/EditProfileView.js